import React, { useState } from "react";
import { Box, Button, Heading, Layer, Text, Tip } from "grommet";
import {
  Group,
  Clock,
  Download,
  CreditCard,
  StatusGood,
  StatusCritical,
} from "grommet-icons";
import { SecuredImage } from "@shared";
import moment from "moment";
import { GuestService } from "@services";
import { useTranslation } from "react-i18next";

export default function VerifyCheckInModal(props) {
  const { t } = useTranslation();
  const { onSubmit, status, checkinData, bookingId, hotel } = props;
  const [open, setOpen] = useState(false);
  const [zoomedPhoto, setZoomedPhoto] = useState();
  const onOpen = () => setOpen(true);
  const onClose = () => setOpen(false);
  const onCloseZoom = () => setZoomedPhoto(undefined);

  const handleSubmit = (approved) => {
    onClose();
    onSubmit(approved);
  };

  const handleDownloadPdf = () => {
    window.location = GuestService.getPdfURL(bookingId);
  };

  const renderPhoto = (label, photo) => {
    if (!photo) return;

    const onZoom = (photo) => setZoomedPhoto(photo);

    return (
      <Box>
        <Text size={18} weight="bold">
          {label}
        </Text>
        <Button
          pad="small"
          gap="small"
          background="light-2"
          round
          width="200px"
          onClick={() => onZoom(photo)}
        >
          <SecuredImage
            className="imageCheckin"
            filename={photo}
            style={{ borderRadius: 10 }}
          />
        </Button>
      </Box>
    );
  };

  return (
    <div>
      {status === "toverify" ? (
        <Button
          label={t("verifyCheckin.verifyOnline")}
          onClick={onOpen}
          primary
          color="status-warning"
          alignSelf="start"
        />
      ) : (
        <Button
          label={t("common.view")}
          onClick={onOpen}
          primary
          color="brand"
          alignSelf="start"
        />
      )}

      {zoomedPhoto && (
        <Layer
          position="center"
          size="large"
          onClickOutside={onCloseZoom}
          onEsc={onCloseZoom}
          onClick={onCloseZoom}
          style={{ overflow: "auto" }}
        >
          <SecuredImage width="100%" filename={zoomedPhoto} />
        </Layer>
      )}

      {open && checkinData && (
        <Layer position="center" onClickOutside={onClose} onEsc={onClose}>
          <Box
            pad="medium"
            gap="small"
            width="large"
            height="stretch"
            alignSelf="start"
          >
            <Heading level={3} margin="none">
              {t("verifyCheckin.onlineCheckin")}
            </Heading>
            <Box
              margin={{ vertical: "medium" }}
              direction="row"
              gap="medium"
              justify="around"
            >
              <Box>
                <Text weight="bold">{t("verifyCheckin.occupants")}</Text>
                <Box
                  direction="row"
                  pad="small"
                  gap="small"
                  background="light-2"
                  round
                  width="220px"
                  height="100px"
                  align="center"
                  margin={{ vertical: "small" }}
                >
                  <Group size="large" style={{ marginRight: 10 }} />
                  <Box>
                    <Text>{t("verifyCheckin.adults", { count: checkinData.adults })}</Text>
                    <Text>{t("verifyCheckin.children", { count: checkinData.children })}</Text>
                    <Text>{t("verifyCheckin.infants", { count: checkinData.babies })}</Text>
                  </Box>
                </Box>
              </Box>
              <Box>
                <Text weight="bold">{t("verifyCheckin.eta")}</Text>
                <Box
                  direction="row"
                  pad="small"
                  gap="small"
                  background="light-2"
                  round
                  width="220px"
                  height="100px"
                  align="center"
                  margin={{ vertical: "small" }}
                >
                  <Clock size="large" style={{ marginRight: 10 }} />
                  <Box>
                    <Text>
                      {checkinData.arrivalTime
                        ? t("verifyCheckin.arrivalAt", {
                            time: moment()
                              .hour(checkinData.arrivalTime)
                              .minute(0)
                              .format("LT"),
                          })
                        : t("verifyCheckin.noArrivalTime")}
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* This is only used by stripe */}
            {checkinData.paymentIntentId && (
              <Box
                pad="small"
                background={checkinData.preAuthApproved ? "#9affa3" : "#ff9a9a"}
                round
                align="center"
                width="medium"
                margin={{ horizontal: "auto" }}
              >
                <Box direction="row" gap="small">
                  <CreditCard />
                  <Text>
                    {checkinData.preAuthApproved
                      ? t("verifyCheckin.preAuthSuccess")
                      : t("verifyCheckin.preAuthIncomplete")}
                  </Text>
                </Box>
                <a
                  href={`https://dashboard.stripe.com/payments/${checkinData.paymentIntentId}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("verifyCheckin.viewStripeDashboard")}
                </a>
              </Box>
            )}
            {/********/}

            <Box
              margin={{ vertical: "medium" }}
              direction="row"
              gap="medium"
              justify="around"
            >
              {renderPhoto(t("verifyCheckin.photoId"), checkinData.photoIDFront)}
              {renderPhoto(t("verifyCheckin.photoIdBack"), checkinData.photoIDBack)}
              {renderPhoto(t("verifyCheckin.selfie"), checkinData.photoSelfie)}
            </Box>

            {checkinData.aiPreCheck && (
              <Box
                pad="small"
                background={
                  checkinData.aiPreCheck === "positive" ? "#e6f7e6" : "#ffe6e6"
                }
                round
                align="center"
                width="medium"
                margin={{ horizontal: "auto" }}
                gap="xsmall"
              >
                <Box
                  direction="row"
                  gap="small"
                  justify="center"
                  align="center"
                >
                  {checkinData.aiPreCheck === "positive" ? (
                    <StatusGood color="status-ok" />
                  ) : (
                    <StatusCritical color="status-error" />
                  )}
                  <Text>
                    {t("verifyCheckin.aiPrecheck")}{" "}
                    <Text weight="bold">
                      {checkinData.aiPreCheck === "positive"
                        ? t("verifyCheckin.positive")
                        : t("verifyCheckin.negative")}
                    </Text>
                  </Text>
                </Box>
                {checkinData.aiPreCheckDetails && (
                  <Text size="small" color="dark-4" textAlign="center">
                    {checkinData.aiPreCheckDetails}
                  </Text>
                )}
              </Box>
            )}

            {checkinData.signature && (
              <Box align="center">
                <Button onClick={handleDownloadPdf} primary color="accent-3">
                  <Box pad="small" direction="row" align="center" gap="small">
                    <Download />
                    <Text>{t("verifyCheckin.downloadRegistrationCard")}</Text>
                  </Box>
                </Button>
                {hotel.optOutMarketingOptionEnabled &&
                  checkinData.optOutMarketing && (
                    <Text color="status-critical" margin={{ top: "small" }}>
                      {t("verifyCheckin.marketingOptOut")}
                    </Text>
                  )}
              </Box>
            )}

            {status === "toverify" ? (
              <Box direction="row" justify="between">
                <Tip
                  dropProps={{
                    align: { bottom: "top", left: "left" },
                    background: "status-error",
                  }}
                  content={t("verifyCheckin.declineTip")}
                >
                  <Button
                    label={t("verifyCheckin.declineCheckin")}
                    onClick={() => handleSubmit("declined")}
                    primary
                    color="status-error"
                  />
                </Tip>
                <Tip
                  dropProps={{
                    align: { bottom: "top", center: "center" },
                    background: "status-warning",
                  }}
                  content={t("verifyCheckin.precheckTip")}
                >
                  <Button
                    label={t("verifyCheckin.precheckOk")}
                    onClick={() => handleSubmit("precheck")}
                    primary
                    color="status-warning"
                  />
                </Tip>
                <Tip
                  dropProps={{
                    align: { bottom: "top", right: "right" },
                    background: "status-ok",
                  }}
                  content={t("verifyCheckin.approveTip")}
                >
                  <Button
                    label={t("verifyCheckin.approveCheckin")}
                    onClick={() => handleSubmit("approved")}
                    primary
                    color="status-ok"
                  />
                </Tip>
              </Box>
            ) : (
              <Button label={t("common.close")} onClick={onClose} color="dark-3" />
            )}
          </Box>
        </Layer>
      )}
    </div>
  );
}

VerifyCheckInModal.propTypes = {};
VerifyCheckInModal.defaultProps = {};
